import { FC, useMemo, useState } from 'react';
import { Card, ElementContainer, Icon, Select, TextField, withStyles } from 'vopay';
import { Title, Button } from 'UI';
import styles from './AddLabelDialog.module.scss';
import useLabels from '@/hooks/useApi/useLabels';
import { useTranslation } from 'react-i18next';

const cn = withStyles(styles);

type AddLabelDialogProps = {
  accountID: string;
  customerLabels: string[];
  onClose: () => void;
  onAdd: (added: string) => void;
};

const AddLabelDialog: FC<AddLabelDialogProps> = ({ accountID, customerLabels, onClose, onAdd }) => {
  const { fetchingLabels, submitting, labels, addLabel } = useLabels();
  const [label, setLabel] = useState<string | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.Overview.AddLabelDialog' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const [searchTerm, setSearchTerm] = useState('');
  const filteredLabels = useMemo(
    () =>
      labels
        .filter((label) => label.toLowerCase().includes(searchTerm.toLowerCase()) && !customerLabels.includes(label))
        .slice(0, 100)
        .map((label) => ({ label, value: label })),
    [labels, searchTerm, customerLabels],
  );

  async function handleAddLabel() {
    if (!label) return;

    await addLabel(label, accountID);
    onAdd(label);
  }

  return (
    <Card className={cn('addLabelDialog')}>
      <Title level="4" style={{ textAlign: 'center' }}>
        {t('title', { defaultValue: 'Add New Label' })}
      </Title>

      <ElementContainer
        label={
          addingNew ? t('addLabel', { defaultValue: 'Add Label' }) : t('selectLabel', { defaultValue: 'Select Label' })
        }
        padding="0px 0px 8px"
        isBold={false}
        input={
          <Select
            version="2"
            name="addLabelSelect"
            emptyFilterMsg={t('noLabelsFound', { defaultValue: 'No Labels Found' })}
            filterable
            options={filteredLabels}
            renderSelectedValue={(data, { Placeholder }) =>
              addingNew ? t('newLabel', { defaultValue: 'New Label' }) : data ? data.value : <Placeholder />
            }
            renderDropdownFooter={({ close }) => (
              <Button
                iconLeft="add"
                style={{ width: '100%' }}
                onClick={() => {
                  setAddingNew(true);
                  close();
                }}
              >
                {t('newLabel', { defaultValue: 'New Label' })}
              </Button>
            )}
            onChange={(e) => {
              setLabel(e.target.value);
              setAddingNew(false);
            }}
            onFilter={(e) => {
              e.preventDefault();
              e.debounce(() => {
                setSearchTerm(e.filter);
              }, 300);
            }}
            disabled={fetchingLabels || submitting}
            loading={fetchingLabels}
          />
        }
      />

      {addingNew && (
        <TextField
          name="addNewLabel"
          variant="outlineShadow"
          disabled={fetchingLabels || submitting}
          placeholder={t('label', { defaultValue: 'Label' })}
          onChange={(e) => setLabel(e.target.value)}
          onRefChange={(input) => input.current?.focus()}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleAddLabel();
            }
          }}
        />
      )}

      <div className={cn('addLabelFooter')}>
        <Button onClick={onClose}>{tGlobal('Buttons.back')}</Button>

        <Button colorScheme="primary" disabled={!label || submitting} onClick={handleAddLabel}>
          {submitting ? (
            <Icon icon="loader" height="40px" width="40px" />
          ) : (
            t('addLabel', { defaultValue: 'Add Label' })
          )}
        </Button>
      </div>
    </Card>
  );
};

export default AddLabelDialog;
